import {
  Box,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useWatch, type Control, type FieldValues } from 'react-hook-form';
import { useShop } from '../../shop/ShopProvider';
import { EMPTY_SELECT_SLOTS } from './model';
import { usePosCategories } from './queries';

interface PosFieldValues {
  availableInPos: boolean;
  posCategoryId: string;
}

/**
 * "Available in Point of Sale" and the register tab it shows under (as Odoo's product form): the
 * POS category appears only while the product is available in POS. Hidden while the shop has the
 * point of sale switched off.
 */
export function PosFields<T extends FieldValues & PosFieldValues>({
  control: anyControl,
}: {
  control: Control<T>;
}) {
  const control = anyControl as unknown as Control<PosFieldValues>;
  const { settings } = useShop();
  const available = useWatch({ control, name: 'availableInPos' });
  const categories = usePosCategories();
  if (settings?.posEnabled === false) return null;
  const depth = (path: string) => path.split(' / ').length - 1;
  return (
    <Stack spacing={2}>
      <Controller
        control={control}
        name="availableInPos"
        render={({ field }) => (
          <FormControlLabel
            control={
              <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Available in Point of Sale
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Off hides it on the register only
                </Typography>
              </Box>
            }
          />
        )}
      />
      {available && (
        <Controller
          control={control}
          name="posCategoryId"
          render={({ field }) => (
            <TextField
              select
              label="POS category"
              value={field.value}
              onChange={field.onChange}
              helperText="The register tab it shows under"
              slotProps={EMPTY_SELECT_SLOTS}
            >
              <MenuItem value="">
                <Typography color="text.secondary">No POS category</Typography>
              </MenuItem>
              {(categories.data ?? []).map((c) => (
                <MenuItem key={c.id} value={c.id} sx={{ pl: 2 + depth(c.path) * 2.25 }}>
                  {depth(c.path) > 0 && (
                    <Box component="span" sx={{ color: 'text.disabled', mr: 0.75 }}>
                      └
                    </Box>
                  )}
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      )}
    </Stack>
  );
}
